import React, { useState, useEffect, useMemo } from 'react';
import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import {
  Button,
  Checkbox,
  TextField,
  Icon,
  FormField,
  Flex,
  Box,
  Select,
  Tabs,
  Tooltip,
} from '@scale/scaleui-radix';
import { Column } from '@scale/compass-shared/types';
import { ExportToGoogleSheetsContext } from '@scale/compass-shared/cards/export_to_google_sheets';
import { faSearch, faCheck, faXmark, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { SERVICE_ACCOUNT } from '@scale/compass-shared/GoogleSheets';
import { useCompassCard } from '@scale/compass-ui/store';
import { useParseGSheetInfo } from '@scale/compass-ui/apiClient';
import { twMerge } from 'tailwind-merge';
import { compassErrorClassName } from 'react-scale/corp/views/DataCompassCardsV2/utils';
import ParametersTable from './ParametersTable';

type GSheetTab = { id: string | number; name: string };

export const SHEET_NAME_DYNAMIC_COLUMN_NAME = 'sheetName';

function getSheetNameColumn(retrievedOutput: any) {
  const columns = retrievedOutput?.response?.schema || [];
  return columns.find((col: Column) => col.header === SHEET_NAME_DYNAMIC_COLUMN_NAME);
}

const ExportToGoogleSheetCardComponent: CompassCardComponent = ({
  index,
}: CompassCardComponentProps) => {
  const { card, updateCardCanSubmit, updateCardContext } =
    useCompassCard<ExportToGoogleSheetsContext>(index);
  const { retrievedOutput } = useCompassCard(index - 1);

  const context = card.defaultContext;
  const [draftSheetURL, setDraftSheetURL] = useState(context.sheetURL || '');
  const [sheetURL, setSheetURL] = useState(context.sheetURL || '');
  const [exportStrategy, setExportStrategy] = useState<'dynamic' | 'manual'>(
    (context.exportStrategy as 'dynamic' | 'manual') || 'dynamic',
  );
  const [exportToSheet, setExportToSheet] = useState(context.exportToSheet || '');
  const [appendToSheet, setAppendToSheet] = useState(context.appendToSheet || false);

  const hasSheetNameColumn = useMemo(() => {
    if (exportStrategy === 'dynamic') {
      return !!getSheetNameColumn(retrievedOutput);
    }
    return true;
  }, [exportStrategy, retrievedOutput]);

  const {
    data: gSheetInfo,
    isPending: isFetchingGSheetInfo,
    error: parseGSheetInfoError,
    isError: isParseGSheetInfoError,
  } = useParseGSheetInfo({ url: sheetURL });

  // Set initial sheet name if none selected and sheets are available
  useEffect(() => {
    if (
      gSheetInfo?.tabs &&
      gSheetInfo.tabs.length > 0 &&
      exportStrategy === 'manual' &&
      !exportToSheet
    ) {
      setExportToSheet(gSheetInfo.tabs[0].name);
    }
  }, [gSheetInfo, exportStrategy, exportToSheet]);

  const handleFindSheet = () => {
    setSheetURL(draftSheetURL);
  };

  useEffect(() => {
    const canSubmit =
      !!gSheetInfo &&
      !!exportStrategy &&
      ((exportStrategy === 'dynamic' && hasSheetNameColumn) ||
        (exportStrategy === 'manual' && !!exportToSheet));
    updateCardCanSubmit(canSubmit);
    updateCardContext({
      ...context,
      sheetURL,
      exportStrategy,
      exportToSheet,
      appendToSheet,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gSheetInfo, exportStrategy, exportToSheet, hasSheetNameColumn, sheetURL, appendToSheet]);

  return (
    <div className="p-4 space-y-4">
      <Tabs.Root
        value={exportStrategy}
        onValueChange={(value: string) => setExportStrategy(value as 'dynamic' | 'manual')}
      >
        <Tabs.List>
          <Tabs.Trigger value="dynamic">
            <Flex direction="row" gap="2">
              Dynamic
              <Tooltip content="Dynamically create sheets based on column sheetName">
                <Icon icon={faInfoCircle} label="info" size={1} />
              </Tooltip>
            </Flex>
          </Tabs.Trigger>
          <Tabs.Trigger value="manual">
            <Flex direction="row" gap="2">
              Manual
              <Tooltip content="Manually select sheet to write to">
                <Icon icon={faInfoCircle} label="info" size={1} />
              </Tooltip>
            </Flex>
          </Tabs.Trigger>
        </Tabs.List>
      </Tabs.Root>
      {/* Error if no sheetName column, before Sheet URL input */}
      {exportStrategy === 'dynamic' && !hasSheetNameColumn && (
        <div className="mb-4 p-3 bg-red-100 text-red-800 rounded border border-red-200">
          {`No '${SHEET_NAME_DYNAMIC_COLUMN_NAME}' column found in loaded data. You must add a column named '${SHEET_NAME_DYNAMIC_COLUMN_NAME}' to use Dynamic export mode.`}
        </div>
      )}
      <div className="mb-4 p-3 bg-yellow-100 text-yellow-800 rounded border border-yellow-200">
        <strong>Warning:</strong> Any existing sheet data will be overridden. Make a copy of your
        destination sheet if necessary.
      </div>
      <Flex direction="column" gap="4">
        <FormField.Root size="3">
          <FormField.Label
            htmlFor="sheet-url-textfield"
            tooltipContent="Copy and paste directly from the search bar in Google Sheets."
          >
            Destination Sheet URL
          </FormField.Label>
          <Flex gap="2" maxWidth="600px">
            <Box width="100%">
              <TextField.Root
                placeholder="e.g. https://docs.google.com/spreadsheets/d/1w59es..."
                value={draftSheetURL}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDraftSheetURL(e.target.value)
                }
                id="sheet-url-textfield"
                className={twMerge('rounded-sm', !draftSheetURL && compassErrorClassName)}
                data-testid="sheet-url-textfield"
              >
                <TextField.Slot>
                  <Icon
                    size="1"
                    icon={gSheetInfo ? faCheck : parseGSheetInfoError ? faXmark : faSearch}
                    label="Search"
                    color={gSheetInfo ? 'success' : parseGSheetInfoError ? 'error' : undefined}
                  />
                </TextField.Slot>
              </TextField.Root>
            </Box>
            <Button
              variant="outline"
              disabled={isFetchingGSheetInfo || draftSheetURL === ''}
              loading={isFetchingGSheetInfo}
              onClick={handleFindSheet}
              data-testid="find-sheet-button"
            >
              Find sheet
            </Button>
          </Flex>
          <FormField.Description color={isParseGSheetInfoError ? 'error' : 'neutral'}>
            Share sheet with: {SERVICE_ACCOUNT}
          </FormField.Description>
        </FormField.Root>

        {exportStrategy === 'dynamic' && <ParametersTable />}
        {sheetURL && gSheetInfo && (
          <Box pt="0">
            {exportStrategy === 'manual' && (
              <FormField.Root size="3">
                <FormField.Label htmlFor="export-to-sheet-select">Sheet Name</FormField.Label>
                <Select.Root
                  value={exportToSheet}
                  onValueChange={setExportToSheet}
                  data-testid="export-to-sheet-select"
                  disabled={isFetchingGSheetInfo || !gSheetInfo}
                >
                  <Select.Trigger placeholder="Select a sheet..." id="export-to-sheet-select" />
                  <Select.Content>
                    {(gSheetInfo?.tabs as GSheetTab[]).map((tab: GSheetTab) => (
                      <Select.Item key={tab.id.toString()} value={tab.name}>
                        {tab.name}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </FormField.Root>
            )}
            <Flex maxWidth="400px" py="2" gap="2">
              <Checkbox
                checked={appendToSheet}
                onCheckedChange={checked => setAppendToSheet(!!checked)}
              />
              Append to source sheet instead of overwriting. This will append to the end of the
              sheet, and exclude column names.
            </Flex>
          </Box>
        )}
      </Flex>
    </div>
  );
};

export default ExportToGoogleSheetCardComponent;
